<template>
  <div class="space-y-6">
    <!-- Tone Section -->
    <div>
      <h2 class="setting-section">
        {{ $i18nPresenter.t('Tone') }}
      </h2>
      <div v-for="(setting, settingIndex) in toneSettings" :key="settingIndex" class="improve-buttons__wrapper">
        <AIAutoImproveWritingButton
          :setting="setting"
          :tabindex="toneSettingTabindex(settingIndex)"
          :isDefaultCanvasBlocks="isDefaultCanvasBlocks"
          @onImproveWritingButtonClicked="value => improveSlideContent('tone', value, settingIndex)"
          v-aha-emit-action="{ user_input: setting.label }"
        />
        <AIAutoImproveUndoButton
          v-if="setting.isUndoButtonVisible"
          :isRestoringOriginalSlide="isRestoringOriginalSlide"
          @onUndoButtonClicked="handleRestoreOriginalSlide('tone', settingIndex)"
          v-aha-emit-action
        />
      </div>
    </div>

    <!-- Complexity Section -->
    <div class="space-y-6">
      <div>
        <h3 class="setting-section">
          {{ $i18nPresenter.t('Style') }}
        </h3>
        <div v-for="(setting, settingIndex) in complexitySettings" :key="settingIndex" class="improve-buttons__wrapper">
          <AIAutoImproveWritingButton
            :setting="setting"
            :tabindex="complexitySettingTabindex(settingIndex)"
            :isDefaultCanvasBlocks="isDefaultCanvasBlocks"
            @onImproveWritingButtonClicked="value => improveSlideContent('complexity', value, settingIndex)"
            v-aha-emit-action="{ user_input: setting.label }"
          />
          <AIAutoImproveUndoButton
            v-if="setting.isUndoButtonVisible"
            :isRestoringOriginalSlide="isRestoringOriginalSlide"
            @onUndoButtonClicked="handleRestoreOriginalSlide('complexity', settingIndex)"
            v-aha-emit-action
          />
        </div>
      </div>
    </div>

    <!-- Length Section -->
    <div class="space-y-6">
      <div>
        <h3 class="setting-section">
          {{ $i18nPresenter.t('Length') }}
        </h3>
        <div v-for="(setting, settingIndex) in lengthSettings" :key="settingIndex" class="improve-buttons__wrapper">
          <AIAutoImproveWritingButton
            :setting="setting"
            :tabindex="lengthSettingTabindex(settingIndex)"
            :isDefaultCanvasBlocks="isDefaultCanvasBlocks"
            @onImproveWritingButtonClicked="value => improveSlideContent('length', value, settingIndex)"
            v-aha-emit-action="{ user_input: setting.label }"
          />
          <AIAutoImproveUndoButton
            v-if="setting.isUndoButtonVisible"
            :isRestoringOriginalSlide="isRestoringOriginalSlide"
            @onUndoButtonClicked="handleRestoreOriginalSlide('length', settingIndex)"
            v-aha-emit-action
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as Sentry from '@sentry/vue'
import { mapState, mapGetters, mapActions } from 'vuex'
import renderCanvasBlocksUtils from '@/utils/renderCanvasBlocksUtils'

// components
import AIAutoImproveWritingButton from '@/components-v2/presentation-editor/right-panel-container/AIAutoImproveWritingButton.vue'
import AIAutoImproveUndoButton from '@/components-v2/presentation-editor/right-panel-container/AIAutoImproveUndoButton.vue'

export default {
  components: {
    AIAutoImproveWritingButton,
    AIAutoImproveUndoButton,
  },
  data() {
    return {
      toneSettings: [
        { value: 'Make it humorous', label: this.$i18nPresenter.t('Make it Humorous'), icon: '🤩' },
        { value: 'Make it professional', label: this.$i18nPresenter.t('Make it Professional'), icon: '💼' },
      ],
      complexitySettings: [
        { value: 'Make it more analytical', label: this.$i18nPresenter.t('Make it Analytical'), icon: '📊' },
        { value: 'Make it more creative', label: this.$i18nPresenter.t('Make it Creative'), icon: '🎨' },
      ],
      lengthSettings: [
        { value: 'Make it shorter', label: this.$i18nPresenter.t('Shorten it'), icon: '✂️' },
        { value: 'Rewrite the content, sentence by sentence, to make it longer. Add more details if necessary.', label: this.$i18nPresenter.t('Lengthen it'), icon: '✍️' },
      ],
      isRestoringOriginalSlide: false,
    }
  },

  computed: {
    ...mapState({
      slideActive: (state) => state.slideActive,
      presentation: (state) => state.presentation,
    }),
    ...mapGetters('aiAutoImproveSlideContent', ['improveWritingSettings']),
    ...mapGetters('slideType', ['isContentSlide']),

    currentSlideId() {
      return this.slideActive && this.slideActive.id
    },

    presentationId() {
      return this.presentation && this.presentation.id
    },

    isDefaultCanvasBlocks() {
      if (!this.slideActive || !this.isContentSlide || !this.slideActive.canvasBlocks) return false

      return renderCanvasBlocksUtils.isDefaultCanvasBlocks(this.slideActive.canvasBlocks)
    },

    toneSettingTabindex() {
      return (settingIndex) => settingIndex + 1
    },

    complexitySettingTabindex() {
      return (settingIndex) => this.toneSettings.length + settingIndex + 1
    },

    lengthSettingTabindex() {
      return (settingIndex) => this.toneSettings.length + this.complexitySettings.length + settingIndex + 1
    },
  },

  methods: {
    ...mapActions('aiAutoImproveSlideContent', [
      'improveSlideContentAction',
      'restoreOriginalSlideAction',
    ]),

    hideAllUndoButtons() {
      try {
        const settings = [...this.toneSettings, ...this.complexitySettings, ...this.lengthSettings]
        settings.forEach((setting) => this.updateUndoButtonVisibility(setting, false))
      } catch (error) {
        Sentry.captureException(error)
      }
    },

    updateAllButtonStates(value) {
      try {
        const settings = [...this.toneSettings, ...this.complexitySettings, ...this.lengthSettings]
        settings.forEach((setting) => {
          const isActive = setting.value === value
          this.$set(setting, 'isDisabled', !isActive)
          this.$set(setting, 'isImprovingSlide', isActive)
        })
      } catch (error) {
        Sentry.captureException(error)
      }
    },

    resetAllButtonStates() {
      try {
        const settings = [...this.toneSettings, ...this.complexitySettings, ...this.lengthSettings]
        settings.forEach((setting) => {
          this.$set(setting, 'isImprovingSlide', false)
          this.$set(setting, 'isDisabled', false)
        })
      } catch (error) {
        Sentry.captureException(error)
      }
    },

    async improveSlideContent(mode, value, settingIndex) {
      try {
        if (this.isDefaultCanvasBlocks) return

        this.updateAllButtonStates(value)
        await this.improveSlideContentAction({
          mode: 'tone',
          value,
        })

        this.resetAllButtonStates()
        this.hideAllUndoButtons()
        this.showUndoButton(mode, settingIndex)
      } catch (error) {
        Sentry.captureException(error)
      }
    },

    async handleRestoreOriginalSlide(mode, settingIndex) {
      try {
        this.isRestoringOriginalSlide = true
        await this.restoreOriginalSlideAction()
        this.hideUndoButton(mode, settingIndex)
      } catch (error) {
        Sentry.captureException(error)
      } finally {
        this.isRestoringOriginalSlide = false
      }
    },

    showUndoButton(mode, settingIndex) {
      try {
        this.updateUndoButtonVisibility(this[`${mode}Settings`][settingIndex], true)
      } catch (error) {
        Sentry.captureException(error)
      }
    },

    hideUndoButton(mode, settingIndex) {
      try {
        this.updateUndoButtonVisibility(this[`${mode}Settings`][settingIndex], false)
      } catch (error) {
        Sentry.captureException(error)
      }
    },

    updateUndoButtonVisibility(setting, isVisible) {
      this.$set(setting, 'isUndoButtonVisible', isVisible)
    },

    normalizeBlocks(blocks) {
      return Object.keys(blocks).map((blockId) => {
        const { id, ...rest } = blocks[blockId] // Exclude the id
        return rest // Return the block without the id
      })
    },

  },
}
</script>

<style lang="scss" scoped>
.setting-section {
  @apply text-secondary-100 text-sm mb-4;
}
.space-y-6 > * + * {
  margin-top: 1rem;
}
.improve-buttons__wrapper {
  @apply flex justify-between items-center mb-3;
}
</style>
